// frontend/src/modules/reports/pages/financial/useReportFetch.ts
//
// Phase 4 (Reports Frontend), Task 4. Financial-family-only helper (owned
// exclusively by this task, same as the rest of pages/financial/**).
//
// The financial pages call `reportsApi.<x>()` directly (it already unwraps
// `.report`) rather than `useApi` + `reportUrl` (the workforce reference
// pattern in HoursSummaryPage) — this hook gives them the same
// loading/error/status/reload shape `useApi` provides, keyed off a fetcher
// factory instead of a URL string. Passing `null` for the fetcher (e.g. the
// date range hasn't resolved yet) skips the fetch entirely, same as `useApi`
// with a null path.

import * as React from 'react';
import { ApiError } from '../../../../core/api-client';

interface ReportFetchState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  /** HTTP status of the failed request when known (e.g. 403), else null. */
  status: number | null;
}

export interface UseReportFetchResult<T> extends ReportFetchState<T> {
  reload: () => void;
}

export function useReportFetch<T>(
  fetcher: (() => Promise<T>) | null,
  deps: React.DependencyList,
): UseReportFetchResult<T> {
  const [state, setState] = React.useState<ReportFetchState<T>>({
    data: null, loading: fetcher !== null, error: null, status: null,
  });
  const [tick, setTick] = React.useState(0);

  // Ref so the effect doesn't re-run just because the caller passed a new
  // closure identity every render — only `deps` (+ reload ticks) drive refetch.
  const fetcherRef = React.useRef(fetcher);
  fetcherRef.current = fetcher;

  React.useEffect(() => {
    const fn = fetcherRef.current;
    if (fn === null) { setState({ data: null, loading: false, error: null, status: null }); return; }
    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: null, status: null }));
    fn()
      .then((res) => { if (!cancelled) setState({ data: res, loading: false, error: null, status: null }); })
      .catch((err: ApiError | Error) => {
        if (!cancelled) {
          setState({ data: null, loading: false, error: err.message, status: err instanceof ApiError ? err.status : null });
        }
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tick, ...deps]);

  return { ...state, reload: () => setTick((t) => t + 1) };
}
